/**
 * PostHog survey trigger events (QUI-185).
 *
 * These three client-side events gate in-app PostHog surveys. They must fire
 * from posthog-js in the browser — server-side captures cannot trigger surveys.
 */

import { browser } from '$app/environment';
import { analytics } from '$lib/analytics/posthog';

export type QuiltyScoreBand = 'high' | 'mid' | 'low';

/** Session-scoped dedup for login trigger. */
const USER_LOGGED_IN_SESSION_KEY = 'quilty_ph_user_logged_in';

/** Session-scoped dedup prefix — one `score_report_read` per report per tab session. */
const SCORE_REPORT_READ_PREFIX = 'quilty_ph_score_report_read:';

/** Order-scoped dedup — survives reloads so each Stripe session fires once. */
const PURCHASE_COMPLETED_FIRED_KEY = 'quilty_ph_purchase_fired';
const PURCHASE_FIRED_CAP = 50;

/** Scroll ≥90% and dwell ≥45s before counting a report as "read". */
const SCORE_REPORT_SCROLL_THRESHOLD = 0.9;
const SCORE_REPORT_MIN_DWELL_MS = 45_000;

/**
 * Band thresholds aligned with greenlight tiers in `scoring.ts`:
 *   high — 72+ (Greenlight / Strong Greenlight)
 *   mid  — 63-71 (Conditional Greenlight / Development)
 *   low  — 0-62 (Revision / Pass)
 */
export function getQuiltyScoreBand(score: number): QuiltyScoreBand {
  if (score >= 72) return 'high';
  if (score >= 63) return 'mid';
  return 'low';
}

/** Pull the headline Quilty Score from common full-report payload shapes. */
export function extractQuiltyScore(
  data: Record<string, unknown> | null | undefined,
): number | null {
  if (!data) return null;

  const v5 = data.quilty_score_v5 as
    | { score?: unknown; score_gated?: boolean }
    | undefined;
  if (v5?.score_gated) return null;
  if (v5?.score != null) {
    const num = Number(v5.score);
    if (Number.isFinite(num)) return num;
  }

  const legacy = (data.quilty_score as { score?: unknown } | undefined)?.score;
  if (legacy != null) {
    const num = Number(legacy);
    if (Number.isFinite(num)) return num;
  }

  const metadata = data.metadata as { quilty_score?: unknown } | undefined;
  if (metadata?.quilty_score != null) {
    const num = Number(metadata.quilty_score);
    if (Number.isFinite(num)) return num;
  }

  return null;
}

function readPurchaseFiredSessions(): string[] {
  if (!browser) return [];
  try {
    const raw = localStorage.getItem(PURCHASE_COMPLETED_FIRED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function markPurchaseFired(stripeSessionId: string): void {
  if (!browser || !stripeSessionId) return;
  try {
    const list = readPurchaseFiredSessions();
    if (list.includes(stripeSessionId)) return;
    list.push(stripeSessionId);
    while (list.length > PURCHASE_FIRED_CAP) list.shift();
    localStorage.setItem(PURCHASE_COMPLETED_FIRED_KEY, JSON.stringify(list));
  } catch {
    /* storage may be unavailable */
  }
}

/**
 * Fires when a Quilty Score purchase is confirmed (Stripe checkout return).
 * Idempotent per Stripe session id — safe across reloads.
 */
export function trackPurchaseCompletedOnce(
  stripeSessionId: string,
  properties: {
    value_usd?: number;
    currency?: string;
    source?: string;
  } = {},
): void {
  if (!browser || !stripeSessionId) return;
  if (readPurchaseFiredSessions().includes(stripeSessionId)) return;

  analytics.track('purchase_completed', {
    stripe_session_id: stripeSessionId,
    currency: properties.currency ?? 'USD',
    source: properties.source ?? 'client',
    ...properties,
  });
  markPurchaseFired(stripeSessionId);
}

/**
 * Fires once per browser session on the first authenticated in-app page after
 * login or session restore.
 */
export function trackUserLoggedInOnce(userId: number | string): void {
  if (!browser || userId == null) return;
  try {
    if (sessionStorage.getItem(USER_LOGGED_IN_SESSION_KEY)) return;
    sessionStorage.setItem(USER_LOGGED_IN_SESSION_KEY, '1');
    analytics.track('user_logged_in', { user_id: userId });
  } catch {
    /* tracking must never break UX */
  }
}

/**
 * Fires once per report per browser session when the reader scrolls ≥90% and
 * has spent ≥45s on the page. Requires a valid Quilty Score for band segmentation.
 */
export function setupScoreReportReadTracking(options: {
  reportKey: string | number;
  quiltyScore: number | null;
  screenplayId?: number;
  shareSlug?: string;
}): () => void {
  if (!browser) return () => {};

  const { reportKey, screenplayId, shareSlug } = options;
  const score = options.quiltyScore;
  if (score == null || !Number.isFinite(score)) return () => {};

  const storageKey = `${SCORE_REPORT_READ_PREFIX}${reportKey}`;
  try {
    if (sessionStorage.getItem(storageKey)) return () => {};
  } catch {
    return () => {};
  }

  const startTime = Date.now();
  let fired = false;

  function scrollDepth(): number {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return 1;
    return Math.min(1, window.scrollY / scrollable);
  }

  function maybeFire(): void {
    if (fired) return;
    try {
      if (sessionStorage.getItem(storageKey)) {
        fired = true;
        return;
      }
    } catch {
      return;
    }

    const elapsed = Date.now() - startTime;
    if (elapsed < SCORE_REPORT_MIN_DWELL_MS) return;

    const depth = scrollDepth();
    if (depth < SCORE_REPORT_SCROLL_THRESHOLD) return;

    fired = true;
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch {
      /* continue — still emit the event */
    }

    analytics.track('score_report_read', {
      quilty_score_band: getQuiltyScoreBand(score),
      quilty_score: score,
      screenplay_id: screenplayId,
      share_slug: shareSlug,
      dwell_ms: elapsed,
      scroll_depth_pct: Math.round(depth * 100),
    });
  }

  const onScroll = () => maybeFire();
  const interval = window.setInterval(maybeFire, 5_000);
  window.addEventListener('scroll', onScroll, { passive: true });

  return () => {
    window.clearInterval(interval);
    window.removeEventListener('scroll', onScroll);
  };
}
